'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';
import { schedulePointerEventsCleanup } from '@/lib/utils/pointer-events-cleanup';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, style, ...props }, ref) => {
  React.useEffect(() => {
    return () => schedulePointerEventsCleanup('Tooltip');
  }, []);

  return (
    <TooltipPortal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-[999999] overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-xl animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        style={{
          backgroundColor: '#0f172a', // slate-900
          color: '#ffffff', // white
          borderColor: '#334155', // slate-700
          ...style,
        }}
        {...props}
      />
    </TooltipPortal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * TooltipButton - A button wrapper that enables tooltips on disabled buttons.
 * Disabled elements don't trigger pointer events, so we wrap in a span.
 *
 * Usage:
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipButton asChild>
 *       <Button disabled>My Button</Button>
 *     </TooltipButton>
 *     <TooltipContent>Tooltip text</TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 */
const TooltipButton = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<typeof TooltipTrigger> & {
    /** Additional className for the wrapper span */
    wrapperClassName?: string;
  }
>(({ children, wrapperClassName, ...props }, ref) => (
  <TooltipTrigger asChild {...props}>
    <span ref={ref} className={wrapperClassName}>
      {children}
    </span>
  </TooltipTrigger>
));
TooltipButton.displayName = 'TooltipButton';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,  TooltipButton };
